import { DateTime } from 'luxon';

export const MAX_CANCELLED_ITEMS_PER_SLOT = 2;

const FULL_WIDTH = 100;
const MINUTES_PER_SLOT = 30;

export type WeekViewLayoutUnit = {
  cancelledItems?: ScheduleItemBase[];
  endDateTime: string;
  leftPercent: number;
  scheduledItem?: ScheduleItemBase;
  startDateTime: string;
  width: number;
};

type UnpositionedUnit = Omit<WeekViewLayoutUnit, 'leftPercent' | 'width'> & {
  endMillis: number;
  isCancelled: boolean;
  startMillis: number;
};

const getSlotKey = (startDateTime: string): string => {
  const { hour, minute } = DateTime.fromISO(startDateTime);
  return `${hour}-${minute < MINUTES_PER_SLOT ? 0 : 1}`;
};

const toUnit = (scheduledItem: ScheduleItemBase): UnpositionedUnit => ({
  endDateTime: scheduledItem.endDateTime,
  endMillis: DateTime.fromISO(scheduledItem.endDateTime).toMillis(),
  isCancelled: !!scheduledItem.isCancelled,
  scheduledItem,
  startDateTime: scheduledItem.startDateTime,
  startMillis: DateTime.fromISO(scheduledItem.startDateTime).toMillis(),
});

const toGroupUnit = (cancelledItems: ScheduleItemBase[]): UnpositionedUnit => {
  const startMillis = Math.min(
    ...cancelledItems.map((item) =>
      DateTime.fromISO(item.startDateTime).toMillis(),
    ),
  );
  const endMillis = Math.max(
    ...cancelledItems.map((item) =>
      DateTime.fromISO(item.endDateTime).toMillis(),
    ),
  );

  return {
    cancelledItems,
    endDateTime: DateTime.fromMillis(endMillis).toISO(),
    endMillis,
    isCancelled: true,
    startDateTime: DateTime.fromMillis(startMillis).toISO(),
    startMillis,
  };
};

const buildUnits = (
  scheduledItems: ScheduleItemBase[],
  isCancelledGroupingEnabled: boolean,
): UnpositionedUnit[] => {
  if (!isCancelledGroupingEnabled) return scheduledItems.map(toUnit);

  const cancelledItemsBySlot = new Map<string, ScheduleItemBase[]>();
  const units: UnpositionedUnit[] = [];

  scheduledItems.forEach((scheduledItem) => {
    if (!scheduledItem.isCancelled) {
      units.push(toUnit(scheduledItem));
      return;
    }
    const slotKey = getSlotKey(scheduledItem.startDateTime);
    cancelledItemsBySlot.set(slotKey, [
      ...(cancelledItemsBySlot.get(slotKey) ?? []),
      scheduledItem,
    ]);
  });

  cancelledItemsBySlot.forEach((cancelledItems) => {
    if (cancelledItems.length > MAX_CANCELLED_ITEMS_PER_SLOT) {
      units.push(toGroupUnit(cancelledItems));
      return;
    }
    units.push(...cancelledItems.map(toUnit));
  });

  return units;
};

const assignColumns = (units: UnpositionedUnit[]): WeekViewLayoutUnit[] => {
  const sortedUnits = [...units].sort(
    (a, b) =>
      a.startMillis - b.startMillis ||
      Number(b.isCancelled) - Number(a.isCancelled) ||
      b.endMillis - a.endMillis,
  );

  const positionedUnits: WeekViewLayoutUnit[] = [];
  let cluster: UnpositionedUnit[] = [];
  let columnEndMillis: number[] = [];
  let columnByUnitIdx: number[] = [];
  let clusterEndMillis = -Infinity;

  const flushCluster = () => {
    const width = Math.round(FULL_WIDTH / (columnEndMillis.length || 1));
    cluster.forEach((unit, idx) => {
      positionedUnits.push({
        cancelledItems: unit.cancelledItems,
        endDateTime: unit.endDateTime,
        leftPercent: columnByUnitIdx[idx] * width,
        scheduledItem: unit.scheduledItem,
        startDateTime: unit.startDateTime,
        width,
      });
    });
  };

  sortedUnits.forEach((unit) => {
    if (unit.startMillis >= clusterEndMillis) {
      flushCluster();
      cluster = [];
      columnEndMillis = [];
      columnByUnitIdx = [];
      clusterEndMillis = -Infinity;
    }

    const freeColumnIdx = columnEndMillis.findIndex(
      (endMillis) => endMillis <= unit.startMillis,
    );
    const columnIdx =
      freeColumnIdx === -1 ? columnEndMillis.length : freeColumnIdx;

    columnEndMillis[columnIdx] = unit.endMillis;
    columnByUnitIdx.push(columnIdx);
    cluster.push(unit);
    clusterEndMillis = Math.max(clusterEndMillis, unit.endMillis);
  });

  flushCluster();

  return positionedUnits;
};

export const getWeekViewDayLayout = (
  scheduledItems: ScheduleItemBase[],
  isCancelledGroupingEnabled: boolean,
): WeekViewLayoutUnit[] => {
  if (!scheduledItems?.length) return [];

  return assignColumns(buildUnits(scheduledItems, isCancelledGroupingEnabled));
};

export const getSlotOffsetInPx = (
  startDateTime: string,
  slotHeight: number,
): number => {
  const { minute } = DateTime.fromISO(startDateTime);
  return ((minute % MINUTES_PER_SLOT) / MINUTES_PER_SLOT) * slotHeight;
};
